import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import KundaliChart, { useChartStyle } from "../components/KundaliChart";
import SEO from "../components/SEO";
import api from "../lib/api";
import {
  Sparkle,
  MapPin,
  CaretRight,
  CaretLeft,
  CalendarBlank,
  Clock,
  User,
  Compass,
  HandSwipeLeft,
  CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import PremiumTeaser from "../components/PremiumTeaser";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

/* ─────────────────────────────────────────────────────────────────────────
 * Natural planetary friendship (Parashari nAisargika-maitri).
 * Referenced against the LORD of the sign a planet sits in. Used to promote
 * the backend's "neutral" dignity to Friend / Enemy when there's a stronger
 * signal available.
 * Rahu/Ketu follow Saturn / Mars respectively (a common Nadi convention).
 * ─────────────────────────────────────────────────────────────────────── */
const FRIENDSHIP = {
  Sun:     { friends: ["Moon", "Mars", "Jupiter"],     enemies: ["Venus", "Saturn"] },
  Moon:    { friends: ["Sun", "Mercury"],              enemies: [] },
  Mars:    { friends: ["Sun", "Moon", "Jupiter"],      enemies: ["Mercury"] },
  Mercury: { friends: ["Sun", "Venus"],                enemies: ["Moon"] },
  Jupiter: { friends: ["Sun", "Moon", "Mars"],         enemies: ["Mercury", "Venus"] },
  Venus:   { friends: ["Mercury", "Saturn"],           enemies: ["Sun", "Moon"] },
  Saturn:  { friends: ["Mercury", "Venus"],            enemies: ["Sun", "Moon", "Mars"] },
  Rahu:    { friends: ["Venus", "Saturn", "Mercury"],  enemies: ["Sun", "Moon", "Mars"] },
  Ketu:    { friends: ["Sun", "Moon", "Jupiter"],      enemies: ["Mercury", "Venus"] },
};

/** Return a UI-ready dignity label + color set for a planet. */
function computeDignity(planet, dignity, rashiLord) {
  if (dignity === "exalted")     return { label: "Exalted",     tone: "green" };
  if (dignity === "debilitated") return { label: "Debilitated", tone: "red" };
  if (dignity === "mooltrikona") return { label: "Mooltrikona", tone: "amber" };
  if (dignity === "own_sign")    return { label: "Own sign",    tone: "gold" };

  // "neutral" branch — promote to Friend / Enemy using natural friendship
  const table = FRIENDSHIP[planet];
  if (table && rashiLord) {
    if (rashiLord === planet)            return { label: "Own sign", tone: "gold" };
    if (table.friends.includes(rashiLord)) return { label: "Friend",   tone: "blue" };
    if (table.enemies.includes(rashiLord)) return { label: "Enemy",    tone: "orange" };
  }
  return { label: "Neutral", tone: "slate" };
}

/** Tailwind class tuples per tone. Solid pill + text color. */
const DIGNITY_TONES = {
  green:  { bg: "bg-emerald-100",    text: "text-emerald-800",    ring: "ring-emerald-200" },
  red:    { bg: "bg-rose-100",       text: "text-rose-800",       ring: "ring-rose-200" },
  amber:  { bg: "bg-amber-100",      text: "text-amber-900",      ring: "ring-amber-300" },
  gold:   { bg: "bg-[#FFF3D6]",      text: "text-[#8A5A00]",      ring: "ring-[#E8A628]/40" },
  blue:   { bg: "bg-sky-100",        text: "text-sky-800",        ring: "ring-sky-200" },
  orange: { bg: "bg-orange-100",     text: "text-orange-800",     ring: "ring-orange-200" },
  slate:  { bg: "bg-slate-100",      text: "text-slate-600",      ring: "ring-slate-200" },
};

/* ─────────────────────────────────────────────────────────────────────────
 * Combustion (Ansta) — Parashari orbs. Rahu/Ketu never combust; Sun itself
 * is the source of light. Moon is only considered combust close to New-Moon.
 * Mercury & Venus have separate direct-vs-retrograde orbs (some texts).
 * ─────────────────────────────────────────────────────────────────────── */
const COMBUST_ORB = {
  Moon:    { direct: 12, retro: 12 },
  Mars:    { direct: 17, retro: 17 },
  Mercury: { direct: 14, retro: 12 },
  Jupiter: { direct: 11, retro: 11 },
  Venus:   { direct: 10, retro: 8  },
  Saturn:  { direct: 15, retro: 15 },
};
const RASHI_INDEX = {
  Aries: 0, Taurus: 1, Gemini: 2, Cancer: 3, Leo: 4, Virgo: 5,
  Libra: 6, Scorpio: 7, Sagittarius: 8, Capricorn: 9, Aquarius: 10, Pisces: 11,
};
function natalLongitude(p) {
  if (!p) return null;
  const idx = RASHI_INDEX[p.natal_rashi];
  if (idx == null) return null;
  return idx * 30 + Number(p.natal_degrees_in_rashi || 0);
}
/** True iff planet is within its combustion orb of Sun (natal). */
function isCombust(planet, planets) {
  if (!COMBUST_ORB[planet]) return false;
  const sun = planets?.Sun;
  const self = planets?.[planet];
  if (!sun || !self) return false;
  const sunLon = natalLongitude(sun);
  const pLon = natalLongitude(self);
  if (sunLon == null || pLon == null) return false;
  let d = Math.abs(pLon - sunLon);
  if (d > 180) d = 360 - d;
  const orbs = COMBUST_ORB[planet];
  const orb = self.retrograde ? orbs.retro : orbs.direct;
  return d < orb;
}

/**
 * Free Kundali — AstroSage-clean interface, but with a decisive upgrade:
 * ONE form → ALL 19 divisional charts (D1 → D60) generated in a single call.
 *
 * Design intent:
 *  • Strip every marketing paragraph — the form should feel as light as a
 *    utility, not a landing page.
 *  • Reuse the /divisional/compute endpoint (already backs
 *    DivisionalChartsPage.jsx) so we deliver Kundali + every varga in a
 *    single API round-trip. D1 (Rasi) is the default view; a horizontal
 *    tab-strip lets users flip through D2, D3, D7, D9, D10, D12, D16,
 *    D20, D24, D27, D30, D40, D45, D60 without recomputing.
 *  • Deep planet-by-planet interpretations remain in the premium
 *    /divisional-charts tool — this page ships only the chart image +
 *    core position table, which is what 95% of casual seekers want.
 */
export default function KundaliCalculatorPage() {
  const navigate = useNavigate();
  const chartStyle = useChartStyle();

  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [place, setPlace] = useState("");
  const [timezone, setTimezone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [selectedDiv, setSelectedDiv] = useState("1");
  const [swipeDelta, setSwipeDelta] = useState(0);
  const touchStartRef = useRef(null);
  // Track the latest delta in a ref too — reading React state inside
  // touchend closes over a possibly-stale value on some iOS builds because
  // setState from touchmove hasn't flushed yet.
  const swipeDeltaRef = useRef(0);

  // One-shot prefill from signed-in user profile (shared hook)
  useBirthDetailsPrefill((bd, u) => {
    if (u?.name) setName((prev) => prev || u.name);
    if (bd?.date) setDate((prev) => prev || bd.date);
    if (bd?.time) {
      const [hh, mm] = String(bd.time).split(":");
      setHour((prev) => (prev !== "" ? prev : hh || ""));
      setMinute((prev) => (prev !== "" ? prev : mm || ""));
    }
    if (bd?.place) setPlace((prev) => prev || bd.place);
    if (bd?.timezone) setTimezone((prev) => prev || bd.timezone);
  });

  // Place autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(false);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const wrapperRef = useRef(null);

  const canSubmit = date && hour !== "" && minute !== "" && place && timezone;

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onPlaceChange = (val) => {
    setPlace(val);
    setPicked(false);
    setTimezone("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = val.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setSearching(false);
      setShowSuggestions(trimmed.length > 0);
      return;
    }
    setSearching(true);
    setShowSuggestions(true);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data: sug } = await api.get(`/places/search`, { params: { q: trimmed } });
        if (myReqId !== reqIdRef.current) return;
        setSuggestions(Array.isArray(sug) ? sug : []);
        setShowSuggestions(true);
      } catch (_) {
        if (myReqId !== reqIdRef.current) return;
        setSuggestions([]);
      } finally {
        if (myReqId === reqIdRef.current) setSearching(false);
      }
    }, 250);
  };

  const pickSuggestion = (s) => {
    setPlace(s.name);
    setTimezone(s.timezone);
    setPicked(true);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const time = `${hour}:${minute}`;
      // Single call → all 19 vargas
      const { data: r } = await api.post("/divisional/compute", { date, time, place, timezone });
      setData(r);
      setSelectedDiv("1"); // Default to D1 (Rasi)
      setTimeout(() => {
        document.getElementById("kundali-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate your kundali. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setData(null);
    setSelectedDiv("1");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
  };

  const chart = useMemo(() => data?.charts?.[selectedDiv], [data, selectedDiv]);
  // Public free-tool switcher intentionally hides D11 (Rudramsha) — it's a
  // niche varga and less relevant for casual seekers than D1–D10, D12+ core
  // vargas. It stays available inside the paid /divisional-charts tool.
  //
  // Order intentionally deviates from the backend's numeric sort: casual
  // seekers care most about D1 (self), D9 (marriage/dharma), D10 (career)
  // and D2 (wealth). Everything else falls in after in numeric order so the
  // tab-strip and dropdown lead with the four charts people actually search
  // AstroKuberChat / AstroSage / Prokerala for on Google.
  const PRIORITY = [1, 9, 10, 2];
  const supportedDivisions = useMemo(() => {
    const raw = (data?.supported_divisions || []).filter((d) => String(d) !== "11");
    const nums = raw.map((d) => Number(d));
    const front = PRIORITY.filter((d) => nums.includes(d));
    const rest = nums.filter((d) => !PRIORITY.includes(d)).sort((a, b) => a - b);
    return [...front, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ── Swipe navigation between vargas ──
  const currentIdx = supportedDivisions.findIndex((d) => String(d) === selectedDiv);
  const goToDiv = (idx) => {
    const clamped = Math.max(0, Math.min(supportedDivisions.length - 1, idx));
    setSelectedDiv(String(supportedDivisions[clamped]));
  };
  const goNext = () => goToDiv(currentIdx + 1);
  const goPrev = () => goToDiv(currentIdx - 1);

  const onSwipeStart = (e) => {
    touchStartRef.current = e.targetTouches[0].clientX;
    swipeDeltaRef.current = 0;
    setSwipeDelta(0);
  };
  const onSwipeMove = (e) => {
    if (touchStartRef.current == null) return;
    const raw = e.targetTouches[0].clientX - touchStartRef.current;
    // Cap the visual translate at ±80px so the card feels tactile
    const dx = Math.max(-80, Math.min(80, raw));
    swipeDeltaRef.current = dx;
    setSwipeDelta(dx);
  };
  const onSwipeEnd = () => {
    if (touchStartRef.current == null) return;
    const dx = swipeDeltaRef.current;
    touchStartRef.current = null;
    swipeDeltaRef.current = 0;
    setSwipeDelta(0);
    const THRESHOLD = 50;
    if (dx <= -THRESHOLD) goNext();
    else if (dx >= THRESHOLD) goPrev();
  };

  // Convert the divisional payload → the shape KundaliChart expects
  const chartShape = useMemo(() => {
    if (!chart) return null;
    return {
      lagna: { rashi: chart.lagna.rashi },
      planets: Object.fromEntries(
        Object.entries(chart.planets).map(([planet, p]) => [
          planet,
          { rashi: p.rashi, retrograde: !!p.retrograde },
        ]),
      ),
    };
  }, [chart]);

  return (
    <PageShell>
      <SEO
        title="Free Kundali Online — All 19 Divisional Charts (D1–D60), Instantly"
        description="Compute your free North-Indian Kundali plus all 19 Vedic divisional charts (D1–D60) in a single click. Swiss-Ephemeris-accurate, Lahiri sidereal — used by every AstroKuber reading."
        path="/kundali-calculator"
        keywords={[
          "free kundali",
          "free kundli online",
          "divisional charts",
          "D1 D9 D10 D60",
          "varga chart calculator",
          "vedic birth chart",
          "north indian kundali",
          "janam kundli online",
          "shodashvarga free",
          "lahiri ayanamsha kundali",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "AstroKuber Free Kundali & Divisional Charts",
          applicationCategory: "AstrologyApplication",
          operatingSystem: "Any (Web)",
          offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
          description:
            "Free Vedic birth chart and all 19 Shodashvarga divisional charts (D1–D60) with Swiss Ephemeris and Lahiri ayanamsha.",
        }}
      />

      <div
        className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-24"
        data-testid="kundali-calculator-page"
      >
        {/* ── Compact but stunning hero. Only shown on the form screen. ── */}
        {!data && (
          <div className="mb-5 relative" data-testid="kc-hero">
            {/* Decorative glow orb — soft gold aura behind the title */}
            <div
              aria-hidden="true"
              className="absolute -top-8 -right-6 w-36 h-36 rounded-full blur-3xl opacity-50 pointer-events-none"
              style={{ background: "radial-gradient(circle, #F7D774 0%, transparent 70%)" }}
            />
            {/* Second smaller orb on the left for balance */}
            <div
              aria-hidden="true"
              className="absolute -top-4 -left-8 w-24 h-24 rounded-full blur-3xl opacity-35 pointer-events-none"
              style={{ background: "radial-gradient(circle, #C97C0A 0%, transparent 70%)" }}
            />

            <span
              className="relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase mb-2.5 shadow-sm"
              style={{
                background: "linear-gradient(135deg, #FFF3D6 0%, #FFE4A8 100%)",
                color: "#8A5A00",
                border: "1px solid rgba(232, 166, 40, 0.35)",
              }}
            >
              <Sparkle size={11} weight="fill" /> Free · No sign-up
            </span>

            <h1
              className="relative font-serif-display text-[26px] sm:text-[32px] leading-[1.05] tracking-tight font-light"
              style={{ color: "#3F2D1B" }}
            >
              Free{" "}
              <span
                className="italic font-normal"
                style={{
                  background: "linear-gradient(135deg, #F7D774 0%, #E8A628 50%, #C97C0A 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Kundali
              </span>
              <span className="mx-1.5 text-[#E8A628]/60">·</span>
              <span className="font-semibold whitespace-nowrap" style={{ color: "#C97C0A" }}>
                D1 → D60
              </span>
            </h1>

            <p className="relative text-[13px] mt-1.5 font-medium" style={{ color: "#7B6952" }}>
              with full{" "}
              <span className="font-semibold" style={{ color: "#3F2D1B" }}>
                planetary placements
              </span>
            </p>

            {/* Gold hairline separator below the hero for a premium finish */}
            <div
              className="mt-3 h-px w-16 rounded-full"
              style={{ background: "linear-gradient(90deg, #E8A628 0%, transparent 100%)" }}
            />
          </div>
        )}

        {/* ── FORM: AstroSage-clean, thick gold-orange rounded inputs. ── */}
        {!data && (
          <form
            onSubmit={onSubmit}
            className="space-y-4"
            data-testid="kundali-form"
          >
            {/* Name */}
            <div>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-[#3F2D1B] mb-1.5">
                <User size={14} weight="duotone" className="text-gold" />
                Name <span className="font-normal text-muted">(optional)</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Anita"
                className="block w-full min-w-0 box-border px-4 py-3 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] focus:ring-4 focus:ring-[#E8A628]/15 outline-none text-[15px] bg-white transition-all"
                data-testid="kc-name-input"
              />
            </div>

            {/* Date of Birth */}
            <div>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-[#3F2D1B] mb-1.5">
                <CalendarBlank size={14} weight="duotone" className="text-gold" />
                Date of Birth
              </label>
              <input
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="block w-full min-w-0 box-border px-4 py-3 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] focus:ring-4 focus:ring-[#E8A628]/15 outline-none text-[15px] bg-white transition-all appearance-none"
                data-testid="kc-date-input"
              />
            </div>

            {/* Time of Birth */}
            <div>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-[#3F2D1B] mb-1.5">
                <Clock size={14} weight="duotone" className="text-gold" />
                Time of Birth
              </label>
              <div className="flex items-center gap-2 min-w-0">
                <select
                  required
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  className="flex-1 min-w-0 box-border px-4 py-3 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] focus:ring-4 focus:ring-[#E8A628]/15 outline-none text-[15px] bg-white transition-all appearance-none"
                  data-testid="kc-hour-input"
                >
                  <option value="">HH</option>
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <span className="text-[#E8A628] font-serif-display text-2xl flex-shrink-0">:</span>
                <select
                  required
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                  className="flex-1 min-w-0 box-border px-4 py-3 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] focus:ring-4 focus:ring-[#E8A628]/15 outline-none text-[15px] bg-white transition-all appearance-none"
                  data-testid="kc-minute-input"
                >
                  <option value="">MM</option>
                  {MINUTES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <span className="text-muted text-xs flex-shrink-0">24-hr</span>
              </div>
            </div>

            {/* Place of Birth */}
            <div ref={wrapperRef} className="relative">
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-[#3F2D1B] mb-1.5">
                <Compass size={14} weight="duotone" className="text-gold" />
                Place of Birth
              </label>
              <div className="relative">
                <input
                  type="text"
                  required
                  placeholder="Start typing your city…"
                  value={place}
                  onChange={(e) => onPlaceChange(e.target.value)}
                  autoComplete="off"
                  className="block w-full min-w-0 box-border px-4 py-3.5 pr-11 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] focus:ring-4 focus:ring-[#E8A628]/15 outline-none text-[15px] bg-white transition-all"
                  data-testid="kc-place-input"
                />
                <MapPin size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#E8A628]" />
              </div>
              {showSuggestions && (
                <div
                  className="absolute top-full left-0 right-0 mt-1.5 max-h-64 overflow-y-auto bg-white rounded-xl border border-[#E8A628]/40 z-20 shadow-lg"
                  data-testid="kc-place-suggestions"
                >
                  {place.trim().length > 0 && place.trim().length < 2 && (
                    <div className="px-4 py-3 text-xs text-muted">Keep typing — at least 2 characters.</div>
                  )}
                  {searching && (
                    <div className="px-4 py-3 text-xs text-muted flex items-center gap-2">
                      <span className="inline-block w-3 h-3 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                      Searching for &ldquo;{place.trim()}&rdquo;…
                    </div>
                  )}
                  {!searching && place.trim().length >= 2 && suggestions.length === 0 && (
                    <div className="px-4 py-3 text-xs text-muted">
                      No matches for &ldquo;{place.trim()}&rdquo;. Try a fuller name or include the country.
                    </div>
                  )}
                  {suggestions.map((s, i) => (
                    <button
                      type="button"
                      key={`${s.lat}-${s.lon}-${i}`}
                      onClick={() => pickSuggestion(s)}
                      className="w-full text-left px-4 py-2.5 hover:bg-[#E8A628]/10 transition-colors border-b border-[#E8A628]/10 last:border-0"
                      data-testid={`kc-place-option-${i}`}
                    >
                      <div className="text-sm text-[#3F2D1B]">{s.name}</div>
                      <div className="text-[11px] text-muted mt-0.5">{s.timezone}</div>
                    </button>
                  ))}
                </div>
              )}
              {picked && timezone && (
                <p className="text-muted text-[11px] mt-1.5">
                  Timezone: <span className="text-gold font-mono">{timezone}</span>
                </p>
              )}
            </div>

            {/* CTA */}
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="relative w-full mt-1 py-3.5 rounded-full font-semibold text-[16px] text-[#3F2D1B] disabled:opacity-50 disabled:cursor-not-allowed transition-transform active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg"
              style={{
                background: "linear-gradient(135deg, #F7D774 0%, #E8A628 55%, #C97C0A 100%)",
              }}
              data-testid="kc-submit-btn"
            >
              <Sparkle size={18} weight="fill" />
              {submitting ? "Casting your chart…" : "Generate my Kundali"}
            </button>
          </form>
        )}

        {/* ── RESULT: back button + chart-switcher + selected chart + planets ── */}
        {data && chart && (
          <div id="kundali-result" className="space-y-5" data-testid="kc-result">
            {/* Back to form — minimal top action, no hero repeat */}
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#E8A628]/40 text-[13px] font-medium text-[#3F2D1B] active:scale-95 transition-transform shadow-sm"
              data-testid="kc-back-btn"
            >
              <CaretLeft size={13} weight="bold" className="text-[#C97C0A]" />
              Back to edit details
            </button>

            {/* Divisional-chart switcher — dropdown selector + pill tabs */}
            <div data-testid="kc-division-tabs">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#C97C0A] mb-2 flex items-center gap-1.5">
                <Sparkle size={12} weight="fill" /> All divisional charts · D1 → D60
              </div>

              {/* Dropdown — quick jump on mobile, one-tap on desktop */}
              <div className="relative mb-3">
                <select
                  value={selectedDiv}
                  onChange={(e) => setSelectedDiv(e.target.value)}
                  className="block w-full min-w-0 box-border appearance-none px-4 py-3 pr-10 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] focus:ring-4 focus:ring-[#E8A628]/15 outline-none text-[14px] font-semibold text-[#3F2D1B] bg-white transition-all"
                  data-testid="kc-div-select"
                  aria-label="Jump to a specific divisional chart"
                >
                  {supportedDivisions.map((d) => {
                    const c = data.charts?.[String(d)];
                    return (
                      <option key={d} value={String(d)}>
                        D{d} — {c?.name}
                      </option>
                    );
                  })}
                </select>
                <CaretDown
                  size={14}
                  weight="bold"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#E8A628] pointer-events-none"
                />
              </div>

              {/* Pill tabs — horizontal scroll */}
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {supportedDivisions.map((d) => {
                  const c = data.charts?.[String(d)];
                  const isActive = String(d) === selectedDiv;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setSelectedDiv(String(d))}
                      className={`flex-shrink-0 snap-start px-4 py-2 rounded-full text-xs font-semibold transition-all active:scale-95 ${
                        isActive
                          ? "text-[#3F2D1B] shadow-md"
                          : "bg-white text-[#7B6952] border border-[#E8A628]/25 hover:border-[#E8A628]/50"
                      }`}
                      style={
                        isActive
                          ? {
                              background: "linear-gradient(135deg, #F7D774 0%, #E8A628 55%, #C97C0A 100%)",
                            }
                          : undefined
                      }
                      data-testid={`kc-div-tab-${d}`}
                    >
                      D{d}
                      <span className={`ml-1 font-normal ${isActive ? "text-[#3F2D1B]/70" : "text-muted"}`}>
                        {c?.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected chart card — swipeable with visual hint */}
            <div
              className="rounded-2xl p-5 sm:p-6 relative overflow-hidden select-none"
              style={{
                background: "linear-gradient(135deg, #FFF8E7 0%, #FFE7C2 100%)",
                border: "1px solid rgba(232, 166, 40, 0.35)",
                transform: swipeDelta ? `translateX(${swipeDelta * 0.6}px)` : undefined,
                transition: swipeDelta ? "none" : "transform 260ms cubic-bezier(0.2, 0.9, 0.3, 1)",
                touchAction: "pan-y",
              }}
              onTouchStart={onSwipeStart}
              onTouchMove={onSwipeMove}
              onTouchEnd={onSwipeEnd}
              data-testid="kc-selected-chart"
            >
              {/* Header row with chart id + prev/next arrows */}
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={currentIdx <= 0}
                  aria-label="Previous divisional chart"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/70 border border-[#E8A628]/30 disabled:opacity-30 disabled:cursor-not-allowed active:scale-90 transition-all shadow-sm"
                  data-testid="kc-prev-chart"
                >
                  <CaretLeft size={14} weight="bold" className="text-[#C97C0A]" />
                </button>

                <div className="text-center flex-1 min-w-0">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#C97C0A]">
                    {chart.label} · {chart.name}
                  </div>
                  <h2 className="font-serif-display text-2xl mt-0.5 font-light text-[#3F2D1B] truncate">
                    Lagna in <span className="text-gold">{chart.lagna.rashi}</span>
                  </h2>
                  <div className="text-[11px] text-muted mt-0.5">Lord: {chart.lagna.rashi_lord}</div>
                </div>

                <button
                  type="button"
                  onClick={goNext}
                  disabled={currentIdx >= supportedDivisions.length - 1}
                  aria-label="Next divisional chart"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/70 border border-[#E8A628]/30 disabled:opacity-30 disabled:cursor-not-allowed active:scale-90 transition-all shadow-sm"
                  data-testid="kc-next-chart"
                >
                  <CaretRight size={14} weight="bold" className="text-[#C97C0A]" />
                </button>
              </div>

              {/* ▲ Impactful description callout — replaces the tiny italic. */}
              <div
                className="mt-4 relative rounded-xl p-3.5 pl-4 overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #3F2D1B 0%, #6B4520 60%, #C97C0A 100%)",
                }}
                data-testid="kc-chart-purpose"
              >
                <span
                  className="absolute left-0 top-0 bottom-0 w-1"
                  style={{ background: "linear-gradient(180deg, #F7D774 0%, #E8A628 100%)" }}
                />
                <div className="flex items-start gap-2.5">
                  <div
                    className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(247, 215, 116, 0.25)" }}
                  >
                    <Sparkle size={12} weight="fill" style={{ color: "#F7D774" }} />
                  </div>
                  <div>
                    <div
                      className="text-[10px] font-bold tracking-[0.18em] uppercase mb-1"
                      style={{ color: "#F7D774" }}
                    >
                      What this chart reveals
                    </div>
                    <div
                      className="text-[13px] leading-relaxed font-medium"
                      style={{ color: "#FFF8E7" }}
                    >
                      {chart.purpose}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chart image */}
              <div className="mt-4 flex justify-center bg-white/50 rounded-xl p-3">
                {chartShape && (
                  <KundaliChart
                    kundali={chartShape}
                    size={typeof window !== "undefined" && window.innerWidth < 640 ? 300 : 360}
                  />
                )}
              </div>

              {/* ← Swipe indicator: pulses gently to invite the gesture. */}
              <div
                className="mt-3 flex items-center justify-center gap-2 text-[10px] font-medium tracking-wider uppercase"
                style={{ color: "#C97C0A" }}
                data-testid="kc-swipe-hint"
              >
                <CaretLeft size={11} weight="bold" className="animate-[bounce_1.4s_ease-in-out_infinite]" />
                <HandSwipeLeft size={14} weight="duotone" />
                <span>Swipe to switch charts</span>
                <CaretRight size={11} weight="bold" className="animate-[bounce_1.4s_ease-in-out_infinite]" />
              </div>

              <div className="text-[10px] text-muted text-center mt-1.5 font-light">
                {chart.label} · {chartStyle === "south" ? "South-Indian style" : "North-Indian style"}
              </div>
            </div>

            {/* Compact planet position table for the selected varga */}
            <div className="rounded-2xl p-5 bg-white border border-[#E8A628]/25" data-testid="kc-planets-table">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#C97C0A] mb-3">
                Planet positions in {chart.label}
              </div>
              {/* Small legend so the color-coded pills are immediately readable */}
              <div className="flex flex-wrap gap-1.5 mb-3 text-[9px] uppercase tracking-wider font-semibold">
                {[
                  ["Exalted", "green"],
                  ["Own sign", "gold"],
                  ["Friend", "blue"],
                  ["Neutral", "slate"],
                  ["Enemy", "orange"],
                  ["Debilitated", "red"],
                ].map(([lbl, tone]) => {
                  const t = DIGNITY_TONES[tone];
                  return (
                    <span key={lbl} className={`px-1.5 py-0.5 rounded ${t.bg} ${t.text}`}>
                      {lbl}
                    </span>
                  );
                })}
                {/* Extra chips: retrograde + combust states */}
                <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800">R℞ Retrograde</span>
                <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">🔥 Combust</span>
              </div>
              <div className="divide-y divide-[#E8A628]/15">
                {Object.entries(chart.planets).map(([planet, p]) => {
                  const dg = computeDignity(planet, p.dignity, p.rashi_lord);
                  const tone = DIGNITY_TONES[dg.tone];
                  // Rahu & Ketu (chhaya-grahas) are always retrograde by
                  // definition — they orbit against the zodiac direction.
                  // Some backends omit the flag for shadow planets, so we
                  // enforce it here for correctness.
                  const retrograde = p.retrograde || planet === "Rahu" || planet === "Ketu";
                  const combust = isCombust(planet, chart.planets);
                  return (
                    <div
                      key={planet}
                      className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 py-2 text-sm"
                      data-testid={`kc-planet-row-${planet}`}
                    >
                      {/* Left: planet name + sign + degree/nakshatra + R/C chips */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#3F2D1B]">{planet}</span>
                          <span className="text-xs text-muted">H{p.house_from_lagna ?? p.house}</span>
                          <span className="text-xs text-[#3F2D1B]">{p.rashi}</span>
                          {retrograde && (
                            <span
                              className="inline-flex items-center px-1.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-orange-100 text-orange-800 ring-1 ring-orange-200"
                              title="Retrograde"
                              data-testid={`kc-retro-${planet}`}
                            >
                              R℞
                            </span>
                          )}
                          {combust && (
                            <span
                              className="inline-flex items-center px-1.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 ring-1 ring-rose-200"
                              title="Combust — within Sun's rays, weakened significations"
                              data-testid={`kc-combust-${planet}`}
                            >
                              🔥 Combust
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted font-mono mt-0.5">
                          {p.divisional_degrees_in_rashi}° · {p.divisional_nakshatra} p{p.divisional_pada}
                        </div>
                      </div>
                      {/* Right: dignity pill */}
                      <span
                        className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ring-1 flex-shrink-0 ${tone.bg} ${tone.text} ${tone.ring}`}
                        data-testid={`kc-dignity-${planet}`}
                      >
                        {dg.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Premium teaser + specialist CTA */}
            <PremiumTeaser
              testid="kc-premium-teaser"
              topLine={
                <>
                  You&apos;re looking at 19 charts. There&apos;s still a lot underneath.
                  <br />
                  <span className="text-amber-300">Unlock 55+ pages of what&apos;s next.</span>
                </>
              }
              subLine={
                <>
                  Your <b className="text-amber-100">{data.charts?.["1"]?.lagna?.rashi} lagna</b> and current dasha
                  set the direction — the full 20-year timeline decodes your Sade Sati window, 6 doshas,
                  gem &amp; rudraksha recommendations, and year-by-year career/marriage/wealth/health scores.
                </>
              }
            />

            <div data-testid="kc-cta-card">
              <ToolAstrologerCta
                requiredTechnique="Divisional Charts (D1–D10)"
                pitch={`Get a live walk-through of your ${chart.label} chart from a 16-Varga (Shodashvarga) specialist`}
                ctaLabel="Talk to"
              />
            </div>

            {/* Deep-dive nudge to the paid divisional charts tool */}
            <button
              type="button"
              onClick={() => navigate("/divisional-charts")}
              className="w-full flex items-center justify-between gap-2 px-5 py-4 rounded-2xl bg-white border-2 border-[#E8A628]/40 hover:border-[#E8A628] transition-colors text-left"
              data-testid="kc-deepdive-btn"
            >
              <div>
                <div className="text-sm font-semibold text-[#3F2D1B]">
                  Want planet-by-planet insights?
                </div>
                <div className="text-xs text-muted mt-0.5">
                  Dignity, deity, retrograde impact &amp; interpretations for every planet in every varga.
                </div>
              </div>
              <CaretRight size={20} weight="bold" className="text-[#E8A628] flex-shrink-0" />
            </button>
          </div>
        )}
      </div>
    </PageShell>
  );
}
